<template>
    <treeselect
      :value="selection"
      :options="options"
      :placeholder="placeholder"
      :load-options="loadOptions"
      :normalizer="normalizer"
      :default-expand-level="2"
      :disabled="disabled"
      valueFormat="object"
      value-consists-of="LEAF_PRIORITY"
      sort-value-by="INDEX"
      multiple
      disableFuzzyMatching
      class="splicing-treeselect"
      @input="selectObjects"
    />
</template>

<script>
import InputField from "@/components/Home/Forms/_Molecules/InputField";
import _ from "lodash";
import { mapActions } from "vuex";
import { bus } from "@/main";

export default {
  name: "ClosureFormSplicingTabObjectOptions",

  components: { InputField },

  props: {
    objectEndpoint: String,

    objectUuid: String,

    side: String,

    inputLabel: String,

    selection: Array,

    cables: Array,

    splitters: Array,

    placeholder: String,

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      options: null
    };
  },

  computed: {
    cableType() {
      if (this.side === "origin") {
        return "in";
      }
      if (this.side === "destination") {
        return "out";
      }
    }
  },

  watch: {
    cables() {
      this.options = null;
    },

    splitters() {
      this.options = null;
    }
  },

  beforeDestroy() {
    this.removeHighlightFromCurrentCableSelection();
  },

  methods: {
    ...mapActions(["fetchCablesInObject", "fetchSplittersInObject"]),

    async loadOptions({ callback }) {
      if (!this.cables) {
        await this.getObjectCables(this.cableType);
      }

      this.options = await this.prepareObjectOptions(this.cables);

      callback();
    },

    async getObjectCables(cableType) {
      try {
        let cables = await this.fetchCablesInObject({
          objectEndpoint: this.objectEndpoint,
          objectUUID: this.objectUuid,
          cableType: cableType
        });
        cables = cables.filter(
          cable => cable.fibers && cable.fibers.length > 0
        );

        this.$emit(
          "set-cables",
          _.orderBy(cables, ["cb_label", "cb_id"], ["asc", "asc"])
        );
      } catch (e) {
        console.error(e);
      }
    },

    async prepareObjectOptions() {
      let cablesList = {
        id: "cables",
        label: this.$i18n.tc("Cable", 2),
        children: this.cables
      };

      let splittersList = {
        id: "splitters",
        label: this.$i18n.tc("Equipment", 2),
        children: this.splitters
      };

      let options = [];
      if (this.cables && this.cables.length > 0 && this.splitters && this.splitters.length > 0) {
        options = [
          {
            id: "all",
            label: this.$i18n.t("All"),
            children: [cablesList,  splittersList]
          }
        ];
      } else if (this.cables && this.cables.length === 1) {
        options = this.cables;
      } else if (this.cables && this.cables.length > 1) {
        options.push(cablesList);
      } else if (this.splitters && this.splitters.length === 1) {
        options = this.splitters;
      } else if (this.splitters && this.splitters.length > 1) {
        options.push(splittersList);
      }
      return options;
    },

    normalizer(node) {
      if (this.isCable(node)) {
        let label = node.cb_label ? node.cb_label : node.cb_id;
        return {
          id: node.cb_id,
          label: "Cb: " + label
        };
      } else if (this.isSplitter(node)) {
        let label = node.sp_label;
        if (node.hasOwnProperty("inPorts") && node.hasOwnProperty("outPorts")) {
          let ratio = node.inPorts.length + ":" + node.outPorts.length;
          label += " (" + ratio + ")";
        }
        return {
          id: node.sp_uuid,
          label: label
        };
      }

      return {
        id: node.id,
        label: node.label
      };
    },

    selectObjects(newSelection) {
      if(newSelection.length !== this.selection.length){
        if (this.selection.length > 0) {
          this.removeHighlightFromCurrentCableSelection();
        }
        const lists = this.getSplittersWaterfall(newSelection);
        const finalSplitter = [];
        lists.splittersList.forEach(item => {
          item.splitters.forEach(splitter => {
            finalSplitter.push(splitter)
          });
        });
        this.$emit("select-objects", {cables: lists.cablesList, splitters: finalSplitter});

        if (newSelection.length > 0) {
          this.addHighlightToNewCableSelection(newSelection);
        }
      }
    },
    getSplittersWaterfall(selection){
      const splittersList = [{splitters: []}, {splitters: []}, {splitters: []}];
      const cablesList = [];
      selection.forEach(element => {
        if(!this.isCable(element)){
          const hierarchy = this.getSplitterHierarchy(element);
          if(hierarchy >= 0){
            splittersList[hierarchy].splitters.push(element);
          }
        }else{
          cablesList.push(element)
        }
      });
      return {splittersList, cablesList};
    },
    getSplitterHierarchy(splitter){
      const splitters = [];
      this.getSpilttersConnected(splitter.sp_id, splitters);
      const jsonObject = splitters.map(JSON.stringify);
      const uniqueSet = new Set(jsonObject);
      const uniqueArray = Array.from(uniqueSet).map(JSON.parse);
      return (uniqueArray.length - 1);
    },
    getSpilttersConnected(spId, spArray){
      //check if splitter was already added
      let hasOutportConnected = false;
      const findSplitter = spArray.find(item => item.sp_id === spId);
      if(!!findSplitter || !(!!spId)){
        return;
      }
      const splitter = this.splitters.find(splitter => splitter.sp_id === spId);
      let nextSpId;
      if(splitter){
        splitter.inPorts.forEach(port => {
          if(!!port.connectedPort){
            hasOutportConnected = true;
            const checkSplitterExists = spArray.find(item => item.sp_id === port.connectedPort.pt_splitter_id);
            if(!(!!checkSplitterExists)){
              nextSpId = port.connectedPort.pt_splitter_id;
              spArray.push({
                sp_id: splitter.sp_id,
              });
            }
          }
        });
      }
      if(splitter && !hasOutportConnected){
        spArray.push({
          sp_id: splitter.sp_id,
        });
      }
      this.getSpilttersConnected(nextSpId, spArray);
    },
    removeHighlightFromCurrentCableSelection() {
      for (const object of this.selection) {
        if (this.isCable(object)) {
          bus.$emit(
            "remove-highlight-from-selected-table",
            this.objectUuid + object.cb_uuid
          );
        }
      }
    },

    addHighlightToNewCableSelection(newSelection) {
      for (const object of newSelection) {
        if (this.isCable(object)) {
          bus.$emit("highlight-object-from-selected-table", {
            geoJson: object.cb_geometry.geojson_geom,
            uuid: this.objectUuid + object.cb_uuid
          });
        }
      }
    },

    isCable(object) {
      return object && object.hasOwnProperty("cb_id");
    },

    isSplitter(object) {
      return object && object.hasOwnProperty("sp_uuid");
    }
  }
};
</script>
<style>
.splicing-treeselect{
  width: 95%;
}
.splicing-treeselect .vue-treeselect__control{
  border: none;
  border-radius: 10px;
  box-shadow: 0.2px 1px 2px rgb(142, 139, 139);
}
.splicing-treeselect-left{
  justify-self: end;
}

.splicing-treeselect .vue-treeselect__control .vue-treeselect__value-container .vue-treeselect__multi-value-item-container .vue-treeselect__multi-value-item .vue-treeselect__multi-value-label{
 font-size: 10px !important;
}

</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Splicing/ObjectOptions.vue